import React from "react";
import PropTypes from "prop-types";
import { Box, ButtonBase, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomerInfo from "../CustomerInfo";
import PreOrderVehicle from "./PreOrderVehicle";
import Pricing from "../Pricing";
import PreOrderPricingSummary from "./PreOrderPricingSummary";
import EMPBreadcrumbs from "../../../shared/components/EMPBreadcrumbs/EMPBreadcrumbs";
import { useCrcStatus } from "../../../shared/hooks/useCrcStatus";

const TAB_BAR_BG = "#F5F6F7";
const SELECTED_BG = "#FFFFFF";
const SELECTED_BORDER = "#0562D2";
const SELECTED_TEXT = "#0562D2";
const UNSELECTED_TEXT = "#000000";

const PreOrderTabButton = ({ label, isSelected, onClick, testId }) => (
  <ButtonBase
    onClick={onClick}
    data-testid={testId}
    disableRipple
    sx={{
      flex: 1,
      minHeight: "40px",
      borderRadius: "4px",
      backgroundColor: isSelected ? SELECTED_BG : "transparent",
      border: `1px solid ${isSelected ? SELECTED_BORDER : "transparent"}`,
      transition: "background-color 0.15s ease, border-color 0.15s ease",
    }}
  >
    <Typography
      sx={{
        fontFamily: isSelected ? "FordF1Bold" : "FordF1Medium",
        fontSize: "14px",
        lineHeight: "20px",
        color: isSelected ? SELECTED_TEXT : UNSELECTED_TEXT,
        textTransform: "none",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
  </ButtonBase>
);

PreOrderTabButton.propTypes = {
  label: PropTypes.string.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  testId: PropTypes.string,
};

PreOrderTabButton.defaultProps = {
  testId: undefined,
};

const PreOrderTabs = ({ order, orderId, activeTab, onTabChange, isMock }) => {
  const { t } = useTranslation();
  const isCrc = useCrcStatus();

  const tabItems = [
    {
      title: t("common.categories.customer"),
      content: <CustomerInfo content={order} orderId={orderId} isPreOrder />,
      testId: "preorder-tab-customer-info",
    },
    {
      title: t("common.categories.vehicle"),
      content: <PreOrderVehicle content={order} />,
      testId: "preorder-tab-vehicle",
    },
    {
      title: t("common.categories.pricing"),
      content: isMock ? (
        <PreOrderPricingSummary
          pricingSummary={order?.customerReservation?.pricingSummary}
        />
      ) : (
        <Pricing content={order} orderId={orderId} />
      ),
      testId: "preorder-tab-pricing",
    },
  ];

  return (
    <Box
      component={isMock ? "section" : "div"}
      sx={{
        width: "100%",
        ...(isMock && {
          boxSizing: "border-box",
          py: 4,
          px: { xs: 4, sm: 4, md: 4, lg: 14 },
        }),
      }}
      data-testid="preorder-tabs"
    >
      {isMock && (
        <>
          {!isCrc && (
            <Box sx={{ mb: 4 }}>
              <EMPBreadcrumbs t={t} />
            </Box>
          )}
          <Typography variant="h1" sx={{ color: "text.primary", mb: 4 }}>
            {`${t("common.orderTypes.preorder")} #${orderId}`}
          </Typography>
        </>
      )}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          backgroundColor: TAB_BAR_BG,
          borderRadius: "4px",
          padding: "4px",
        }}
      >
        {tabItems.map((item, index) => (
          <PreOrderTabButton
            key={item.title}
            label={item.title}
            isSelected={activeTab === index}
            onClick={() => onTabChange(index)}
            testId={item.testId}
          />
        ))}
      </Box>

      <Box sx={{ mt: 6, minHeight: "400px" }}>
        {tabItems.map((item, index) => (
          <Box key={item.title} role="tabpanel" hidden={activeTab !== index}>
            {activeTab === index && item.content}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

PreOrderTabs.propTypes = {
  order: PropTypes.object,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  activeTab: PropTypes.number.isRequired,
  onTabChange: PropTypes.func.isRequired,
  isMock: PropTypes.bool,
};

PreOrderTabs.defaultProps = {
  order: null,
  isMock: false,
};

export default PreOrderTabs;
